import React from 'react';

import { useIntl } from 'react-intl';

import { LIST_SERVICE_PROVIDERS_RESOURCE } from 'src/features/serviceProvider/api/useListServiceProviders';
import { useUpdateServiceProvider } from 'src/features/serviceProvider/api/useUpdateServiceProvider';
import ServiceProviderFormModal from 'src/features/serviceProvider/components/form/ServiceProviderFormModal';
import { organisationMessages } from 'src/features/serviceProvider/utils/messages';
import { ServiceProvider } from 'src/features/serviceProvider/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface ServiceProviderFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServiceProvider: ServiceProvider;
    children?: React.ReactNode;
}

function UpdateServiceProviderFormModal<TData>({
    className,
    semantics,
    children,
    selectedServiceProvider,
}: ServiceProviderFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdateServiceProvider({
        onSuccess: ({ name }) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SERVICE_PROVIDERS_RESOURCE] });
            toast({
                title: formatMessage(organisationMessages.updateOrganisationSuccess),
                description: formatMessage(organisationMessages.updateOrganisationSuccessDescription, {
                    entry: name,
                }),
            });
        },
    });

    return (
        <ServiceProviderFormModal
            selectedServiceProvider={selectedServiceProvider}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ data, providerId: selectedServiceProvider.id, parkingId }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(organisationMessages.editOrganisationSubtitle, {
                entry: selectedServiceProvider.name,
            })}
        >
            {children}
        </ServiceProviderFormModal>
    );
}

export default UpdateServiceProviderFormModal;
